'use client';

// npm library imports
import { useMemo, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

// style imports
import styles from './navbar.module.scss';

// lib imports
import { NAVLINKS } from '@lib/navdata';

// image imports
import crocLogo from '@public/images/logos/logo-white.png';

// component imports
import Icons from '@components/Icons';
import _ContactDropdown from '@components/Navbar/_ContactDropdown';
import SlideMenu from '@components/Navbar/_Slidemenu';

/**
 * Navbar component with sidemenus and contact dropdown
 *
 * @returns The Navbar component JSX.Element
 *
 * @example
 * ```tsx
 * <Navbar />
 * ```
 */
const Navbar = () => {
    // State for contact dropdown visibility
    const [contactVisible, setContactVisible] = useState<boolean>(false);

    // State for slide menu visibility
    const [menuVisible, setMenuVisible] = useState<boolean>(false);

    /**
     * Creates menu navigation items from NAVLINKS data
     *
     * Example of navlink data array
     * ``` ts
     * [
     *     {
     *         title: "Home",
     *         path: "/"
     *     },
     *     {
     *         title: "Menu 2",
     *         path: "/page/menu-2"
     *     },
     * ]
     * ```
     */
    const renderMenuItems = useMemo(() => {
    return NAVLINKS.map((link) => (
        <li key={link.path}>
            <Link href={link.path}>{link.title}</Link>
        </li>
    ));
}, []);

    return (
        <>
            {/* Navbar Container */}
            <nav className={styles.navbar}>
                {/* Burger Menu */}
                <div className={styles.burger}>
                    {/* Menu open button */}
                    <button
                        aria-label="Menu"
                        onClick={(e) => {
                            e.preventDefault();
                            setMenuVisible((prev) => !prev);
                        }}
                        className={styles[`icon-btn`]}
                        aria-expanded={menuVisible}
                    >
                        {/* Burger Icon */}
                        <Icons
                            iconClass={`nav-list`}
                            externalStlyes={styles.burgerAlign}
                            globalStyles='hover'
                        />
                    </button>
                    {/* Logo */}
                    <div className={styles.logo}>
                        <Link href="/">
                            <Image
                                priority
                                src={crocLogo}
                                width={150}
                                height={35}
                                alt="Crocodile Hunter Lodge Logo"
                                placeholder="blur"
                            />
                        </Link>
                    </div>
                </div>
                {/* Menu Navigation Items */}
                <div className={styles.navitems}>
                    <ul>{renderMenuItems}</ul>
                </div>
                {/* Book Now and Contact Information */}
                <div className={styles.booknow}>
                    {/* Book Now link */}
                    <div>
                        <Link href="https://bookings.thecrocodilehunterlodge.com.au/search-rates">
                            BOOK NOW
                        </Link>
                    </div>
                    {/* Divider */}
                    <div className={styles.spanLine}>{`|`}</div>
                    {/* Contact Information button */}
                    <button
                        aria-label="Contact Information"
                        className={`${styles['icon-btn']} ${styles.phone}`}
                        onClick={(e) => {
                            e.preventDefault();
                            setContactVisible((prev) => !prev);
                        }}
                        aria-expanded={contactVisible}
                    >
                        {/* Phone Icon */}
                        <Icons
                            iconClass={`general-telephone-fill`}
                            extraClasses={['hover']}
                        />
                    </button>
                </div>
            </nav>
            {/* Contact Dropdown Component with toggle function */}
            <_ContactDropdown
                contactVisible={contactVisible}
                toggle={() => setContactVisible((prev) => !prev)}
            />
            {/* Slide Menu Component with toggle function */}
            <SlideMenu
                menuVisibility={menuVisible}
                toggle={() => {
                    setMenuVisible((prev) => !prev);
                }}
            />
        </>
    );
};

export default Navbar;
